import { ImageIcon, Video } from 'lucide-react';
import { isVideoUrl } from '@/services/supportTicketApi';

interface TicketAttachmentGalleryProps {
  urls?: string[] | null;
  label?: string;
  className?: string;
}

/**
 * Renders the files attached to a support ticket or one of its replies: image
 * thumbnails that open full-size in a new tab, and an inline player for a
 * screen recording. Both kinds live in one URL list on the ticket.
 */
export default function TicketAttachmentGallery({ urls, label = 'Image', className = '' }: TicketAttachmentGalleryProps) {
  const all = (urls || []).filter(Boolean);
  const videos = all.filter(isVideoUrl);
  const images = all.filter((u) => !isVideoUrl(u));
  if (all.length === 0) return null;

  return (
    <div className={`space-y-3 ${className}`}>
      {images.length > 0 && (
        <div>
          <div className="flex items-center gap-1 text-[11px] text-gray-500 mb-1.5">
            <ImageIcon className="h-3 w-3" />
            {images.length} {images.length === 1 ? 'image' : 'images'}
          </div>
          <div className="flex flex-wrap gap-2">
            {images.map((url, i) => (
              <a
                key={`${url}-${i}`}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="h-16 w-16 rounded-lg border border-gray-200 overflow-hidden bg-gray-50 hover:ring-2 hover:ring-indigo-400 transition-shadow"
                title={`${label} ${i + 1}`}
              >
                <img src={url} alt={`${label} ${i + 1}`} className="h-full w-full object-cover" loading="lazy" />
              </a>
            ))}
          </div>
        </div>
      )}
      {videos.map((url, i) => (
        <div key={`${url}-${i}`}>
          <div className="flex items-center gap-1 text-[11px] text-gray-500 mb-1.5">
            <Video className="h-3 w-3" /> Video
          </div>
          <video src={url} controls preload="metadata" className="w-full max-w-md rounded-lg border border-gray-200 bg-black" />
          <a href={url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-indigo-600 hover:underline">
            Open video in new tab
          </a>
        </div>
      ))}
    </div>
  );
}
